import { apiClient } from './apiClient';

export const billingService = {
  /**
   * Get billing summary for a specific service type
   * @param {number} companyId - Company ID
   * @param {string} serviceType - 'CMMS' or 'CHATBOT'
   * @returns {Promise<BillingSummaryResponse>}
   */
  getSummary(companyId, serviceType) {
    return apiClient.get(`/api/billing/company/${companyId}/summary?serviceType=${serviceType}`);
  },

  /**
   * Get all invoices for a company
   * @param {number} companyId - Company ID
   * @returns {Promise<InvoiceResponse[]>}
   */
  getInvoices(companyId) {
    return apiClient.get(`/api/billing/company/${companyId}/invoices`);
  },

  /**
   * Create a Stripe Checkout session for plan upgrade
   * @param {Object} data - Checkout data
   * @param {number} data.companyId - Company ID
   * @param {string} data.serviceType - 'CMMS' or 'CHATBOT'
   * @param {number} data.planId - Plan ID
   * @param {string} data.billingPeriod - 'MONTHLY' or 'ANNUAL'
   * @returns {Promise<{checkoutUrl: string, sessionId: string}>}
   */
  createCheckoutSession(data) {
    return apiClient.post('/api/billing/checkout', data);
  },

  /**
   * Get proration preview for a paid-to-paid plan change
   * @param {number} companyId
   * @param {{ serviceType: string, planId: number, billingPeriod: string }} params
   * @returns {Promise<{proratedAmount, nextBillingDate, paymentMethodBrand, paymentMethodLast4}>}
   */
  getChangePlanPreview(companyId, { serviceType, planId, billingPeriod }) {
    return apiClient.get(`/api/billing/company/${companyId}/change-plan-preview?serviceType=${serviceType}&planId=${planId}&billingPeriod=${billingPeriod}`);
  },

  /**
   * Execute a paid-to-paid plan change
   * @param {number} companyId
   * @param {{ serviceType: string, planId: number, billingPeriod: string }} data
   */
  changePlan(companyId, { serviceType, planId, billingPeriod }) {
    return apiClient.post(`/api/billing/company/${companyId}/change-plan`, { serviceType, planId, billingPeriod });
  },

  /**
   * Bypass-mode: directly activate a subscription without Stripe.
   * @param {number} companyId
   * @param {{ serviceType: string, planId: number, billingPeriod: string, companyEmail?: string, companyName?: string }} data
   */
  activateSubscription(companyId, { serviceType, planId, billingPeriod, companyEmail, companyName }) {
    return apiClient.post(`/api/billing/company/${companyId}/activate`, {
      serviceType, planId, billingPeriod, companyEmail, companyName,
    });
  },

  /**
   * Preview invoice as HTML (returns text/html string).
   * Uses raw fetch to get HTML content.
   */
  async previewInvoice(companyId, invoiceNumber) {
    const res = await fetch(`/api/billing/company/${companyId}/invoices/${encodeURIComponent(invoiceNumber)}/preview`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to load invoice preview');
    return res.text();
  },

  /**
   * Preview receipt as HTML (returns text/html string).
   */
  async previewReceipt(companyId, invoiceNumber) {
    const res = await fetch(`/api/billing/company/${companyId}/invoices/${encodeURIComponent(invoiceNumber)}/receipt`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Failed to load receipt preview');
    return res.text();
  },

  /**
   * Resend invoice + receipt email for an invoice.
   */
  resendEmail(companyId, invoiceNumber) {
    return apiClient.post(`/api/billing/company/${companyId}/invoices/${encodeURIComponent(invoiceNumber)}/resend-email`);
  },

  /**
   * Get chatbot subscription details (includes maxChatbots from plan).
   * @param {number} companyId
   */
  getChatbotSubscription(companyId) {
    return apiClient.get(`/api/subscriptions/chatbot/company/${companyId}`);
  },

  /**
   * Cancel a subscription immediately.
   * @param {number} companyId
   * @param {string} serviceType - 'CMMS' or 'CHATBOT'
   */
  cancelSubscription(companyId, serviceType) {
    const path = serviceType === 'CMMS'
      ? `/api/subscriptions/cmms/${companyId}`
      : `/api/subscriptions/chatbot/${companyId}`;
    return apiClient.delete(path);
  },

  /**
   * Enable or disable overage charges for a subscription.
   * @param {number} companyId
   * @param {string} serviceType - 'CMMS' or 'CHATBOT'
   * @param {boolean} enabled
   */
  toggleOverage(companyId, serviceType, enabled) {
    const path = serviceType === 'CMMS'
      ? `/api/subscriptions/cmms/${companyId}/overage-toggle?enabled=${enabled}`
      : `/api/subscriptions/chatbot/${companyId}/overage-toggle?enabled=${enabled}`;
    return apiClient.put(path);
  },

  // ── Chatbot message blocks ──────────────────────────────────────────────────

  /**
   * Get available chatbot message blocks for purchase.
   * @param {number} companyId
   * @returns {Promise<ChatbotBlockDefinition[]>}
   */
  getAvailableChatbotBlocks: async (companyId) => {
    return apiClient.get(`/api/billing/company/${companyId}/chatbot/blocks/available`);
  },

  /**
   * Purchase a chatbot message block.
   * @param {number} companyId
   * @param {number} blockDefId - Block definition ID
   * @returns {Promise<{chatbotBlockPurchase}>}
   */
  buyChatbotBlock: async (companyId, blockDefId) => {
    return apiClient.post(`/api/billing/company/${companyId}/chatbot/blocks/buy`, { blockDefId });
  },

  /**
   * Get the active chatbot message block for a company.
   * @param {number} companyId
   * @returns {Promise<ChatbotBlock|null>}
   */
  getActiveChatbotBlock: async (companyId) => {
    const response = await fetch(
      `/api/billing/company/${companyId}/chatbot/blocks/active`,
      { credentials: 'include' }
    );
    if (response.status === 204) return null;
    if (!response.ok) throw new Error('Failed to fetch active chatbot block');
    return response.json();
  },

  // ── CMMS slot packs ─────────────────────────────────────────────────────────

  /**
   * Get available CMMS slot packs for purchase.
   * @param {number} companyId
   * @param {string} [resourceType] - Optional filter by resource type
   * @returns {Promise<CmmsSlotPackDefinition[]>}
   */
  getAvailableCmmsSlotPacks: async (companyId, resourceType) => {
    const url = resourceType
      ? `/api/billing/company/${companyId}/cmms/slots/available?resourceType=${resourceType}`
      : `/api/billing/company/${companyId}/cmms/slots/available`;
    return apiClient.get(url);
  },

  /**
   * Purchase a CMMS slot pack.
   * @param {number} companyId
   * @param {number} slotPackDefId - Slot pack definition ID
   * @returns {Promise<{cmmsSlotPurchase}>}
   */
  buyCmmsSlot: async (companyId, slotPackDefId) => {
    return apiClient.post(`/api/billing/company/${companyId}/cmms/slots/buy`, { slotPackDefId });
  },

  /**
   * Get all active CMMS slot packs for a company.
   * @param {number} companyId
   * @returns {Promise<CmmsSlot[]>}
   */
  getActiveCmmsSlots: async (companyId) => {
    return apiClient.get(`/api/billing/company/${companyId}/cmms/slots/active`);
  },

  // ── Renewal management ──────────────────────────────────────────────────────

  toggleAutoRenewal: async (companyId, serviceType, enabled) => {
    const type = serviceType.toLowerCase(); // 'cmms' or 'chatbot'
    return apiClient.put(
      `/api/subscriptions/${type}/${companyId}/auto-renewal?enabled=${enabled}`
    );
  },

  cancelSlot: async (companyId, slotPurchaseId) => {
    return apiClient.put(
      `/api/billing/company/${companyId}/cmms/slots/${slotPurchaseId}/cancel`
    );
  },

  cancelBlock: async (companyId, blockPurchaseId) => {
    return apiClient.put(
      `/api/billing/company/${companyId}/chatbot/blocks/${blockPurchaseId}/cancel`
    );
  },

  /**
   * Pay an OPEN invoice immediately using the stored payment method.
   * @param {number} companyId
   * @param {string} invoiceNumber - Invoice number (e.g. INV-PRE-1-XXXXX)
   */
  payInvoice: async (companyId, invoiceNumber) => {
    return apiClient.post(
      `/api/billing/company/${companyId}/invoices/${encodeURIComponent(invoiceNumber)}/pay`
    );
  },
};
